<template>
  <div class="grid-graph-container w-full" ref="gridGraphContainer" style="min-height: 360px">
    <div
      v-if="contextMenu.show"
      v-on-clickaway="() => {contextMenu.show = false}"
      class="context-menu"
      :style="{ top: contextMenu.y + 'px', left: contextMenu.x + 'px' }"
    >
      <button @click="copyGridCard" class="w-full flex items-center gap-2 px-2 py-1.5 text-neutral-alpha-800 rounded-md transition-colors duration-100 hover:bg-neutral-alpha-50">
        <BaseLoadingSpinnerCircle v-if="loading.copyCard" class="w-5 h-5"/>
        <CopyIcon v-else/>
        <BaseText type="body" size="sm">
          Copy card
        </BaseText>
      </button>
      <button @click="copyCardCreative" class="w-full flex items-center gap-2 px-2 py-1.5 text-neutral-alpha-800 rounded-md transition-colors duration-100 hover:bg-neutral-alpha-50">
        <BaseLoadingSpinnerCircle v-if="loading.copyCreative" class="w-5 h-5"/>
        <CopyIcon v-else/>
        <BaseText type="body" size="sm">
          Copy {{ contextMenu.originItemRef?.ads?.[0]?.ad_type === 'video' ? 'thumbnail' : 'creative' }}
        </BaseText>
      </button>
    </div>
    <div v-if="contextMenu.show" class="dupe-container top-0 left-0 max-w-xs" ref="dupeContainer" style="z-index: -1">
      <GridGraphCard
        only-image
        :data="contextMenu.originItemRef"
        :data-summary="dataSummary"
        :selected-kpis="selectedKpis"
        :is-ad-level="isAdLevel"
        :is-public="isPublic"
      />
      <div class="p-1.5 pt-2 flex items-center gap-2 justify-center">
        <MetricsIcon class="text-text-muted"/>
      <BaseText type="label" size="xs" class="text-text-muted">Exported from Lens by <span class="underline">foreplay.co</span></BaseText>
      </div>
    </div>
    <div v-for="(item,i) in dataItems" :key="`grid-card-${item.group_by || item.ad_id}`">
      <div class="flex-grow mx-auto h-full" style="max-width: 410px">
        <GridGraphCard
          :ref="`graph-card-${item.group_by}-${i}`"
          :data="item"
          :data-summary="dataSummary"
          :selected-kpis="selectedKpis"
          :is-ad-level="isAdLevel"
          :is-public="isPublic"
          @showGroupBreakdown="$emit('showGroupBreakdown', item)"
          @showDetailsDrawer="$emit('showDetailsDrawer', item)"
          @handleContextOpen="handleContextOpen($event, item)"
          @handleContextClose="handleContextClose"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'
import GridGraphCard from './GridGraphCard.vue'
import { toBlob } from 'html-to-image'
// Icons
import MetricsIcon from '../../../globals/Icons/LensIcons/MetricsIcon.vue'
import CopyIcon from '../../../globals/Icons/CopyIconStatic.vue'
export default {
  name: 'LensGridGraphs',
  mixins: [clickaway],
  components: {
    GridGraphCard,
    CopyIcon,
    MetricsIcon
  },
  props: {
    dataItems: {
      type: Array,
      default: () => []
    },
    dataSummary: {
      type: Object,
      default: () => {}
    },
    selectedKpis: {
      type: Array,
      default: () => []
    },
    isAdLevel: {
      type: Boolean,
      default: false
    },
    isPublic: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      contextMenu: {
        show: false,
        x: 0,
        y: 0,
        originItemRef: null
      },

      loading: {
        copyCard: false,
        copyCreative: false
      }
    }
  },
  methods: {
    isSafari () {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    },
    async copyBlobToClipboard (blob) {
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);
    },
    // Clipboard write doesn't support jpg which all tw images are. Convert to png via canvas write
    async copyJPGasPNG (url) {
      try {
        if (!url) {
          this.$showAlert({ type: 'error', message: 'Failed to fetch creative' })
          return
        }
        const response = await nativeFetch(url, { mode: 'cors' })
        const blob = await response.blob()

        const imageBitmap = await createImageBitmap(blob)
        const canvas = document.createElement('canvas')
        canvas.width = imageBitmap.width
        canvas.height = imageBitmap.height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(imageBitmap, 0, 0)

        // Convert canvas to PNG Blob
        canvas.toBlob(async (pngBlob) => {
          if (this.isSafari()) {
            this.downloadBlob(pngBlob)
          } else {
            const item = new ClipboardItem({ 'image/png': pngBlob })
            await navigator.clipboard.write([item])
            this.$showAlert({ type: 'success', message: 'Creative asset copied to clipboard!' })
          }
          console.log('JPEG converted and copied as PNG!')
        }, 'image/png')
      } catch (e) {
        console.log(e)
        this.$showAlert({ type: 'error', message: 'Error fetching creative asset' })
      }
    },
    async copyCardCreative () {
      this.loading.copyCreative = true
      try {
        const creativeUrl = this.contextMenu.originItemRef?.ads?.[0].ad_image_url;
        if (!creativeUrl) {
          this.$showAlert({ type: 'error', message: 'No ad image url' })
          return
        }

        await this.copyJPGasPNG(creativeUrl)
      } catch (error) {
        console.error('copy creative error:', error)
        this.$showAlert({ type: 'error', message: 'Unable to copy creative asset' })
      } finally {
        this.handleContextClose()
        this.loading.copyCreative = false
      }
    },
    async copyGridCard () {
      this.loading.copyCard = true
      try {
        if (this.isSafari()) {
          this.$showAlert({ type: 'error', message: "Sorry! This feature isn't available on safari." })
          return;
        }
        const dataBlob = await toBlob(document.querySelector('.dupe-container'), {
          skipFonts: true,
          cacheBust: true
        })
        this.copyBlobToClipboard(dataBlob)
        this.$showAlert({ type: 'success', message: 'Card image copied to clipboard!' })
      } catch (error) {
        console.error('create card image error:', error)
        this.$showAlert({type: 'error', message: 'Unable to copy card'})
      } finally {
        this.handleContextClose()
        this.loading.copyCard = false
      }
    },
    downloadBlob (blob) {
      const imageUrl = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = imageUrl
      link.download = 'creative-asset.png'
      link.target = '_blank'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(imageUrl)
      this.$showAlert({
        type: 'success',
        message: 'Image downloaded successfully!'
      })
    },
    handleContextClose () {
      this.contextMenu = {
        show: false,
        x: 0,
        y: 0,
        originItemRef: null
      }
    },
    handleContextOpen (event, item) {
      event.preventDefault()
      this.contextMenu = {
        show: true,
        x: event.clientX,
        y: event.clientY,
        originItemRef: item
      }
    }
  }
}
</script>

<style scoped>
.grid-graph-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(275px, 1fr));
  gap: 4px;
}

/* Context menu styles */
.context-menu {
  position: absolute;
  background: #23242c;
  border-radius: 10px;
  z-index: 1000;
  min-width: 120px;
  max-width: max-content;
  padding: 4px;
}

.dupe-container{
  background: white;
  border-radius: 10px;
  width: 400px;
  position: absolute;
  padding: 4px;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
</style>
